import { useId, useMemo, useState } from 'react';

import { DEFAULT_DIAL, DIALS_LONGEST_FIRST, DIAL_CODES } from '../lib/dial-codes';
import { composeE164, splitE164 } from '../lib/phone';

import s from './PhoneField.module.css';

interface PhoneFieldProps {
  id: string;
  /** Current value as E.164 (or '' when empty). */
  value: string;
  /** Receives the composed E.164 value, or '' while the number part is blank. */
  onChange: (e164: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  describedBy?: string | undefined;
  autoFocus?: boolean;
}

/**
 * Dial code + national number, presented as ONE control.
 *
 * The two elements are unstyled inside a shared bordered shell, and the focus
 * ring is drawn on the shell via :focus-within, so tabbing between the picker
 * and the number never looks like leaving the field. Before this, every phone
 * input in the app was a bare text box demanding hand-typed E.164 — it rejected
 * "+977 981 234 5678" rather than cleaning it, and defaulted to a US example in
 * a Nepal-first product.
 *
 * The dial code is intentionally NOT lifted into form state: only the composed
 * E.164 value is, which keeps validation to the single shared E164_RE and means
 * a caller can round-trip a stored number in without extra plumbing.
 */
export function PhoneField({
  id,
  value,
  onChange,
  invalid = false,
  disabled = false,
  describedBy,
  autoFocus = false,
}: PhoneFieldProps): JSX.Element {
  const selectId = useId();
  const parsed = useMemo(() => splitE164(value, DIALS_LONGEST_FIRST), [value]);

  // The dial code is local state seeded from the incoming value: while the
  // number box is empty the composed value is '', which carries no dial code,
  // so deriving it purely from props would snap the picker back to +977 the
  // moment the user cleared the number.
  const [dial, setDial] = useState<string>(parsed?.dial ?? DEFAULT_DIAL);
  const national = parsed?.national ?? '';

  function handleDial(nextDial: string): void {
    setDial(nextDial);
    onChange(composeE164(nextDial, national));
  }

  return (
    <div className={`${s.shell ?? ''} ${invalid ? (s.invalid ?? '') : ''}`}>
      <span className={s.dialWrap}>
        <select
          id={selectId}
          className={s.dial}
          value={dial}
          disabled={disabled}
          autoComplete="tel-country-code"
          aria-label="Country dial code"
          onChange={(event) => handleDial(event.target.value)}
        >
          {DIAL_CODES.map((country) => (
            <option key={country.iso} value={country.dial}>
              {country.dial} {country.name}
            </option>
          ))}
        </select>
        <span className={s.shown} aria-hidden="true">
          {dial}
          <svg
            className={s.caret}
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2.5 4.5 6 8l3.5-3.5" />
          </svg>
        </span>
      </span>
      <span className={s.divider} aria-hidden="true" />
      <input
        id={id}
        className={s.number}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        placeholder="981 234 5678"
        value={national}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- opt-in only; used by the blocking profile dialog, where this is the first field and focus must land in the modal
        autoFocus={autoFocus}
        onChange={(event) => onChange(composeE164(dial, event.target.value))}
      />
    </div>
  );
}
